<template>
    <div class="file-resource-drag-and-drop vld-parent">
        <element-loader
                :loading="isLoading"
                :width="35"
                :height="35"
        />
        <div
                v-if="includePreviews && (layout === 'stacked') && files.length"
                class="files-preview-wrapper"
        >
            <template
                    v-for="file in files"
            >
                <file-resource-card
                        v-if="value.get(file)"
                        :file-app-resource="value.get(file)"
                        @remove-file="removeFile"
                        :key="value.get(file).uuid"
                />
            </template>
        </div>
        <div
                v-cloak
                @drop.prevent="addFile"
                @dragover.prevent="toggleIsDragging(true)"
                @dragleave.prevent="toggleIsDragging(false)"
                class="drag-and-drop-zone"
                :class="computedClasses"
                :style="componentStyles"
        >
            <b-form-file
                    type="file"
                    :ref="componentUid"
                    v-show="false"
                    multiple="multiple"
                    @input="addFileFromInput($event)"
                    :accept="fileTypes"
                    v-if="renderFileFormComponent"
            />
            <div
                    class="text"
                    v-if="!maxFilesReached"
            >
                <template v-if="showDefaultText">
                    <div class="title">
                        Drag & Drop
                    </div>
                    <div class="subtitle">
                        your files here, or
                        <span
                                class="link"
                                @click="openFileDialog"
                        >browse</span>
                    </div>
                </template>
                <template v-if="showAdditionalText">
                    <div
                            class="title title-link"
                            @click="openFileDialog"
                    >
                        <i class="far fa-plus additional-text-icon"/>
                        Add Another
                    </div>
                </template>
            </div>
            <div
                    class="text"
                    v-else
            >
                <div class="title">
                    Max Files Uploaded
                </div>
            </div>
        </div>
        <div
                v-if="includePreviews && (layout !== 'stacked') && files.length"
                class="files-preview-wrapper"
        >
            <template
                    v-for="file in files"
            >
                <file-resource-card
                        v-if="value.get(file)"
                        :file-app-resource="value.get(file)"
                        @remove-file="removeFile"
                        :key="value.get(file).uuid"
                />
            </template>
        </div>
        <div class="mb-2">
                <span class="text-muted">
                  {{ fileTypes }} files with {{ maxFileSize }}MB max size
                </span>
        </div>
    </div>
</template>

<script>
import './FileResourceDragAndDrop.scss';
import FileResourceCard from '../FileResourceCard/FileResourceCard.vue';
import AppResourceCollection from '@milestoneslabs/milestones-js-engine/src/Data/AppResourceCollection';
import FileAppResource from '@milestoneslabs/milestones-core-js/src/AppResources/FileAppResource';
import ElementLoader from '../../ElementLoader/ElementLoader.vue';
import ShowNotificationMixin from '../../../mixins/ShowNotificationMixin';
import {get} from 'lodash';

export default {
    name: 'FileResourceDragAndDrop',
    mixins: [ShowNotificationMixin],
    components: {
        ElementLoader,
        FileResourceCard,
    },

    props: {
        layout: {
            type: String,
            required: false,
            default: 'default',
            validator: (value) => {
                return ['default', 'single', 'stacked'].indexOf(value) !== -1;
            },
        },
        value: {
            type: AppResourceCollection,
            required: true,
        },
        multiple: {
            type: Boolean,
            required: false,
            default: true,
        },
        maxFiles: {
            type: Number,
            required: false,
            default: 1,
        },
        fileTypes: {
            type: String,
            required: false,
            default: '.jpeg, .jpg, .jpe, .png, .bmp, .svg, .xlsx, .xls, .doc, .docx, .7z, .zip, .pdf',
        },
        maxFileSize: {
            type: Number,
            required: false,
            default: 5,
        },
        includePreviews: {
            type: Boolean,
            required: false,
            default: true,
        },
        componentHeight: {
            type: String,
            required: false,
            default: '8.125rem',
        },
        fileMeta: {
            type: Object,
            default: () => {
                return {};
            },
        },
        filesArePublic: {
            type: Boolean,
            default: false,
        },
        deleteFiles: {
            type: Boolean,
            default: true,
        },
        maxFileName: {
            type: Number,
            default: -1,
        }
    },

    data: () => {
        return {
            files: [],
            initialFiles: [],
            filesToDelete: [],
            isLoading: false,
            renderFileFormComponent: true,
            isDragging: false,
        };
    },

    computed: {
        componentStyles() {
            return {
                height: this.calculateHeight,
                display: this.layout === 'single' && this.files.length ? 'none' : 'flex',
            };
        },
        showDefaultText() {
            return !(
                    (this.layout === 'single' && !!this.files.length) ||
                    (this.layout === 'stacked' && this.files.length)
            );
        },
        showAdditionalText() {
            return !!(this.layout === 'stacked' && this.files.length);
        },
        calculateHeight() {
            if (this.layout === 'stacked' && this.files.length) {
                return '3.125rem';
            }
            return this.componentHeight;
        },
        componentUid() {
            return this._uid;
        },
        maxFilesReached() {
            return this.maxFiles <= this.files.length;
        },
        fileTypesArray() {
            return this.fileTypes.split(',').map((item) => item.trim());
        },
        maxFileSizeKb() {
            return this.maxFileSize * Math.pow(10, 6);
        },
        computedClasses() {
            return {
                dragover: this.isDragging,
            };
        },
    },

    mounted() {
        this.setInitialFiles();
    },

    methods: {
        setInitialFiles() {
            this.initialFiles = [];
            this.value.forEach((fileAppResource) => {
                this.initialFiles.push(fileAppResource.uuid);
            });
            this.files = this.initialFiles;
        },
        openFileDialog() {
            this.$refs[this.componentUid].$el.childNodes[0].click();
        },
        async addFileFromInput(files) {
            this.isLoading = true;
            this.renderFileFormComponent = false;
            const droppedFiles = Array.from(files);
            if (droppedFiles.length > (this.maxFiles - this.files.length)) {
                droppedFiles.length = this.maxFiles - this.files.length;
            }
            for (const droppedFile of droppedFiles) {
                if (
                        (!this.checkFileExists(droppedFile)) &&
                        this.checkFileExtension(droppedFile) &&
                        this.checkFileSize(droppedFile) &&
                        this.checkFileName(droppedFile)
                ) {
                    const file = await this.uploadFile(droppedFile);
                    if (file && file.uuid) {
                        this.value.addResource(file);
                        this.files.push(file.uuid);
                    }

                } else {
                    this.isLoading = false;
                }
            }
            this.$nextTick(() => {
                this.renderFileFormComponent = true;
            });
            this.$emit('input', this.value);
        },
        async addFile(e) {
            this.toggleIsDragging(false);
            this.isLoading = true;
            if (this.maxFilesReached) return;
            const droppedFiles = Array.from(e.dataTransfer.files);
            if (!droppedFiles) return;
            if (droppedFiles.length > (this.maxFiles - this.files.length)) {
                droppedFiles.length = this.maxFiles - this.files.length;
            }
            for (const droppedFile of droppedFiles) {
                if (
                        !this.checkFileExists(droppedFile) &&
                        this.checkFileExtension(droppedFile) &&
                        this.checkFileSize(droppedFile) &&
                        this.checkFileName(droppedFile)
                ) {
                    const file = await this.uploadFile(droppedFile);
                    if (file && file.uuid) {
                        this.value.addResource(file);
                        this.files.push(file.uuid);
                    }
                } else {
                    this.isLoading = false;
                }
            }
            this.$emit('input', this.value);
        },
        async uploadFile(file) {
            this.isLoading = true;
            const fileAppResource = new FileAppResource({});
            try {
                fileAppResource.setAttribute('file', file);
                fileAppResource.setAttribute('public', this.filesArePublic);
                if (this.fileMeta && Object.keys(this.fileMeta).length) {
                    fileAppResource.setAttribute('meta', this.fileMeta);
                }
                await fileAppResource.save();
                fileAppResource.once('saved', () => {
                    this.isLoading = false;
                });
            } catch (e) {
                console.warn(e.response.data);
                let message = `There was an error uploading file ${file.name}`;
                if (e.response.data && e.response.data.errors) {
                    message = '';
                    const errors = get(e.response, 'data.errors.file');
                    errors.forEach(error => {
                        if (typeof error === 'string' && error.startsWith('The file must be a file of type')) {
                            message += 'The file must be a file of type ' + this.fileTypes;
                        } else {
                            message += error;
                        }
                    });
                }
                this.isLoading = false;
                this.showNotification({
                    title: `${file.name} Upload`,
                    message: message,
                    type: 'error',
                });
            }

            return fileAppResource;
        },
        removeFile(file) {
            this.files = this.files.filter((f) => {
                return f !== file.uuid;
            });
            this.value.forget(file.uuid);

            if (this.deleteFiles) {
                this.filesToDelete.push(file);
            }

            this.$emit('input', this.value);
        },
        checkFileExtension(file) {
            const fileExtension = '.' + file.name.split('.').pop().trim();
            return !!this.fileTypesArray.includes(fileExtension.toLowerCase());
        },
        checkFileExists(file) {
            const existingFile = this.files.filter((f) => {
                return (f.name === file.name && f.size === file.size && f.type === file.type);
            });
            return !!existingFile.length;
        },
        checkFileSize(file) {
            if (file.size > this.maxFileSizeKb) {
                this.$emit('fileTooLarge', file);
            }
            return (file.size <= this.maxFileSizeKb);
        },
        checkFileName(file) {
            if (this.maxFileName === -1) {
                return true;
            }
            if (file.name.length > this.maxFileName) {
                this.$emit('fileNameTooLong', file);
            }
            return (file.name.length <= this.maxFileName);
        },
        async deleteRemovedFiles() {
            const promises = [];
            this.filesToDelete.forEach((file) => {
                promises.push(file.delete());
            });
            await Promise.all(promises);
            this.filesToDelete = [];
            this.setInitialFiles();
        },
        toggleIsDragging(mode) {
            this.isDragging = mode;
        },
    },
    watch: {
        isLoading(value) {
            this.$emit('update:isLoading', value);
        },
    }
};
</script>
